import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import React, { useMemo } from "react";
import { useReportsQueriesSelector, useReportsTableSelector } from "../../../store";
import { ClientDealTableRow } from "../../../store/types";
import { SelectOption } from "../../forms";
import { TableCell } from "../Measures-table/TableCell";
import { tableProps, titleColumn } from "./ReportMeasuresTable";
import { TableToolbar } from "./TableToolbar";
import { ReportTableToolbar } from "./ReportTableToolbar";

type Props = { isLoading: boolean; groupByOptions: SelectOption[] };

export const useTimelineTable = ({ isLoading, groupByOptions }: Props) => {
  const { originalCurrency, rowBy, groupBy, measures, characteristics, calculatedFields, indexName } =
    useReportsQueriesSelector();
  const measuresTable = useReportsTableSelector();

  const measureTableColumns = useMemo(() => {
    if (!measuresTable) {
      return [];
    }
    const columns: MRT_ColumnDef<ClientDealTableRow>[] = measuresTable.columns.map<MRT_ColumnDef<ClientDealTableRow>>(
      (col) => {
        return {
          header: col.title,
          id: col.key,
          accessorKey: col.key,
          sortingFn: "basic",
          accessorFn: (row) => row.valueData[col.key],
          Cell: ({ row, column }) => (
            <TableCell
              id={column.id}
              value={row.original.valueData[col.key]}
              postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
              applyNumberFormat={row.original.apply_number_format?.[col.key]}
            />
          ),
          AggregatedCell: ({ row, column }) => (
            <TableCell
              id={column.id}
              value={row.original.valueData[col.key]}
              postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
              aggregated
              applyNumberFormat={row.original.apply_number_format?.[col.key]}
            />
          )
        };
      }
    );

    columns.unshift(titleColumn);

    return columns;
  }, [measuresTable]);

  const table = useMaterialReactTable<ClientDealTableRow>({
    // Common table Props
    ...tableProps,
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableColumnActions: false,
    enableBottomToolbar: false,
    layoutMode: "grid",
    // Specific table Props
    columns: measureTableColumns,
    enableColumnResizing: true,

    data: measuresTable?.rows || [],

    // This are styles for "monthly_view" to make columns be flexible
    columnResizeMode: "onEnd",
    defaultColumn: {
      ...tableProps.defaultColumn,
      size: 10, // To get rid off minimum columns size
      muiTableBodyCellProps: {
        sx: { padding: "8px 12px" }
      },
      muiTableHeadCellProps: {
        sx: { padding: "8px 12px" }
      }
    },

    enableExpanding: true,
    state: { isLoading: isLoading || !measuresTable },
    // Add styling for the expand/collapse column
    // Width must accommodate: (max_depth * 16px margin) + ~36px button
    // With 5 groups + rowBy, max depth is ~5, requiring 80px + 36px = 116px minimum
    muiTableBodyCellProps: ({ column }) => ({
      sx:
        column.id === "mrt-row-expand"
          ? {
              width: "120px",
              maxWidth: "120px",
              minWidth: "120px",
              padding: "0px 5px"
            }
          : {}
    }),
    muiTableHeadCellProps: ({ column }) => ({
      sx:
        column.id === "mrt-row-expand"
          ? {
              width: "120px",
              maxWidth: "120px",
              minWidth: "120px",
              padding: "0px 5px"
            }
          : {}
    }),
    renderTopToolbar: () => {
      return (
        <TableToolbar
          setFullScreen={() => table.setIsFullScreen(!table.getState().isFullScreen)}
          isFullScreen={table.getState().isFullScreen}
        >
          <ReportTableToolbar
            groupByOptions={groupByOptions}
            measures={measures}
            characteristics={characteristics}
            calculatedFields={calculatedFields}
            groupBy={groupBy}
            rowBy={rowBy}
            originalCurrency={originalCurrency}
            indexName={indexName}
            isTableLoading={isLoading}
          />
        </TableToolbar>
      );
    }
  });

  return table;
};
